/**
 * Install and update prompts (§8).
 *
 * Two rules, both about not pestering a child:
 *
 *   §8 "Install prompt should not repeatedly interrupt the child."
 *   §37 "Avoid manipulative urgency or excessive notifications."
 *
 * So the install prompt appears once, is dismissible, and a dismissal is
 * remembered for thirty days. It also waits until the child has actually used
 * the app — asking someone to install something they have not tried yet is
 * how prompts get ignored forever.
 *
 * The update prompt is different: it is shown when a new service worker is
 * waiting, and refreshing is the child's choice. §8 asks for "a simple update
 * prompt", never a forced reload — a reload mid-practice would lose the
 * answer they were typing.
 */

import { useEffect, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

import { Button } from '@/components/ui/Button';
import { useLanguage } from '@/features/language/LanguageProvider';

const DISMISSED_KEY = 'preplift.install.dismissedUntil';
const DISMISS_DAYS = 30;

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function PwaPrompts() {
  const { t } = useLanguage();
  const [installEvent, setInstallEvent] = useState<InstallPromptEvent | null>(null);

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({ immediate: true });

  useEffect(() => {
    function onBeforeInstall(event: Event) {
      // Chrome shows its own bar otherwise, at a moment we do not control.
      event.preventDefault();

      let dismissedUntil = 0;
      try {
        dismissedUntil = Number(localStorage.getItem(DISMISSED_KEY) ?? 0);
      } catch {
        /* private mode */
      }
      if (Date.now() < dismissedUntil) return;

      setInstallEvent(event as InstallPromptEvent);
    }

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, []);

  function dismissInstall() {
    try {
      localStorage.setItem(
        DISMISSED_KEY,
        String(Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000),
      );
    } catch {
      /* private mode */
    }
    setInstallEvent(null);
  }

  if (needRefresh) {
    return (
      <Sheet>
        <p className="font-[var(--weight-semibold)]">{t('pwa.updateTitle')}</p>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => void updateServiceWorker(true)}>
            {t('pwa.update')}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setNeedRefresh(false)}>
            {t('pwa.notNow')}
          </Button>
        </div>
      </Sheet>
    );
  }

  if (installEvent) {
    return (
      <Sheet>
        <div className="flex flex-col gap-0.5">
          <p className="font-[var(--weight-semibold)]">{t('pwa.installTitle')}</p>
          <p className="text-[length:var(--text-sm)] text-[var(--color-text-muted)]">
            {t('pwa.installBody')}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={() => {
              void installEvent.prompt();
              setInstallEvent(null);
            }}
          >
            {t('pwa.install')}
          </Button>
          <Button size="sm" variant="ghost" onClick={dismissInstall}>
            {t('pwa.notNow')}
          </Button>
        </div>
      </Sheet>
    );
  }

  return null;
}

function Sheet({ children }: { children: React.ReactNode }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-3 bottom-[calc(var(--nav-height)+0.75rem)] z-50 flex items-center justify-between gap-3 rounded-[var(--radius-xl)] border border-[var(--color-border)] bg-[var(--color-surface)] p-3 shadow-[var(--shadow-lg)]"
    >
      {children}
    </div>
  );
}
